import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { GlitchText, HUDFrame } from '../components/GlitchText';
import { TerminalBadge } from '../components/TerminalBadge';
import { getGAPBlogPosts, slugify, estimateReadTime, extractExcerpt } from '../lib/gap';

const GAP_CLIENT_ID = "92be4f08-de14-40f3-ad44-93a4b3217b83";

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPosts() {
      const gapPosts = await getGAPBlogPosts(GAP_CLIENT_ID);
      setPosts(gapPosts);
      setLoading(false);
    }
    loadPosts();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <TerminalBadge command="blog" color="#00E5FF" variant="inverted" />
          <h1 className="text-4xl md:text-5xl font-bold mt-6 mb-4">
            <GlitchText>Blog</GlitchText>
          </h1>
          <p className="text-gray-400 text-lg mb-12">Technology, design, and innovation insights from App Studio Pro.</p>
        </motion.div>

        {loading ? (
          <div className="text-center text-gray-500 py-20">Loading posts...</div>
        ) : posts.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
            <HUDFrame className="inline-block p-8">
              <p className="text-gray-400 text-lg mb-2">Blog posts coming soon!</p>
              <p className="text-gray-600 text-sm">We're crafting compelling content about technology, design, and innovation.</p>
            </HUDFrame>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {posts.map((post, i) => (
              <motion.a
                key={post.id}
                href={`/blog/${slugify(post.blog_title)}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="block group p-6 rounded-2xl border border-gray-800 hover:border-[#00E5FF]/30 bg-gray-900/50 hover:bg-gray-900 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border border-[#00E5FF]/30 text-[#00E5FF]">Blog</span>
                  <span className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                  <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />{estimateReadTime(post.blog_content)}</span>
                </div>
                <h2 className="text-xl font-bold text-white group-hover:text-[#00E5FF] transition-colors mb-2">{post.blog_title}</h2>
                <p className="text-gray-400 text-sm leading-relaxed mb-3">{extractExcerpt(post.blog_content)}</p>
                <span className="text-sm text-[#00E5FF] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Read more <ArrowRight className="w-4 h-4" />
                </span>
              </motion.a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
